import React from 'react';
import { useSelector } from 'react-redux';

import { composeTheme, generatePalette } from '@private/payment';

import {
  basePermanentConfig,
  LIGHTNESS_SETTINGS,
  THEME_VARIANT,
} from 'constants/defaultPalette';
import { useGetOrganizationSettings } from 'queries/portalUser';
import { settingsPalette } from 'routes/pages/AuthorizedStack/Settings/PortalSettings/utils/palette';
import { TRootState } from 'store';
import { IUserState } from 'store/userConfiguration';
import { COMPONENTS_MAPPING } from 'theme/constants';
import { THEME } from 'theme/theme';
import { TPalette } from 'utils/types';

export const usePortalTheme = () => {
  const { data: orgnSettings } = useGetOrganizationSettings();

  const { themeVariant } = useSelector<TRootState, IUserState>(
    (state) => state.userConfiguration,
  );

  const palette = React.useMemo(() => {
    let currentPalette = settingsPalette[0].palette;
    const palette = orgnSettings?.activeTheme?.palette;

    if (typeof palette === 'string') {
      const existedPalette = settingsPalette.find((item) => item.id === palette);
      if (existedPalette) {
        currentPalette = existedPalette.palette;
      }
    } else if (palette) {
      currentPalette = palette;
    }
    return currentPalette?.reduce<TPalette>((prev, { hue, name, saturation }) => {
      const lightnessConfig = LIGHTNESS_SETTINGS[themeVariant || THEME_VARIANT.DARK];
      return {
        ...prev,
        [name]: generatePalette({
          hue,
          lightnessConfig:
            lightnessConfig[name as keyof typeof lightnessConfig] || lightnessConfig.base,
          saturation,
        }),
      };
    }, basePermanentConfig);
  }, [orgnSettings, themeVariant]);

  React.useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('APP_PALETTE:', palette);
    }
  }, [palette]);

  const { composedTheme, stringVariables } = React.useMemo(() => {
    return composeTheme({
      components: THEME.components,
      componentsMapping: COMPONENTS_MAPPING,
      palette,
      theme: THEME,
    });
  }, [palette]);

  return { composedTheme, orgnSettings, stringVariables };
};
